<script lang="ts">
	import type { OrderFulfillmentStatus$options } from '$houdini';
	import type { Order, OrderLine } from '$lib/types/Order/order';
	import { formatMonthString } from '$lib/utils/Date';
	import { ORDER_STATE_MAPPING } from '$lib/utils/orderState';
	import { T, getTranslate } from '@tolgee/svelte';
	import SearchInput from '../Input/SearchInput.svelte';
	import LabelledChip from '../Chip/LabelledChip.svelte';
	import MonthSlider from '../Slider/MonthSlider.svelte';
	import { customer } from '$lib/store/customer.store';
	import OrderLineItemList from './List/OrderLineItemList.svelte';
	import TitleHeading from '../Heading/TitleHeading.svelte';

	export let orders: Array<Order> = [];
	// The selected month defaults to the 15th of the current month.
	let selectedMonth: Date = new Date(new Date().setDate(15));
	let searchString: string = '';
	// Orders filtered by month.
	let monthOrders: Array<Order> = orders;
	// The orders that are currently shown in the view based on filter/search options.
	let shownOrderLineItems: Array<OrderLine> = [];
	let activeFilter: OrderFulfillmentStatus$options | null = null;

	const { t } = getTranslate();

	$: (activeFilter || searchString || searchString === '') &&
		filterOrdersBySearchStringAndActiveFilter();
	$: selectedMonth && filterOrdersBySelectedMonth();

	function addFilter(filter: string) {
		const clickedFilter = filter as OrderFulfillmentStatus$options;
		const newFilter = clickedFilter === activeFilter ? null : clickedFilter;
		activeFilter = newFilter;
	}

	function filterOrdersBySearchStringAndActiveFilter() {
		const filters = activeFilter
			? ORDER_STATE_MAPPING.filter((value) => value === activeFilter).map(
					(value) => value as OrderFulfillmentStatus$options
				)
			: [];

		const searchWords = searchString
			.toLowerCase()
			.split(' ')
			.filter((word) => !!word);

		shownOrderLineItems =
			monthOrders
				.filter((order) => {
					return filters.length === 0 || filters.includes(order.fulfillmentStatus);
				})
				.flatMap<OrderLine>((order) => {
					return order.lineItems.edges.map((edge) => ({ ...edge.node, order }) as OrderLine);
				})
				.filter((edge) => {
					return searchWords.length > 0
						? searchWords.some(
								(word) => edge.title.toLowerCase().includes(word) || edge.order.name.includes(word)
							)
						: true;
				}) ?? [];
	}

	function filterOrdersBySelectedMonth() {
		monthOrders = orders.filter((order) => {
			const orderDate = new Date(order.processedAt);
			return (
				orderDate.getMonth() === selectedMonth.getMonth() &&
				orderDate.getFullYear() === selectedMonth.getFullYear()
			);
		});

		shownOrderLineItems = monthOrders.flatMap<OrderLine>((order) =>
			order.lineItems.edges.map((edge) => ({ ...edge.node, order }) as OrderLine)
		);
	}

	function handleMonthSliderClick(previousClick: boolean) {
		selectedMonth = new Date(
			selectedMonth.setMonth(selectedMonth.getMonth() + (previousClick ? -1 : 1))
		);
		filterOrdersBySelectedMonth();
	}
</script>

<div class="flex flex-col w-full">
	<div class="flex flex-col w-full gap-4">
		<div class="flex flex-col gap-7 divide-y-[1px] divide-solid divide-dark-quaternary">
			<TitleHeading title={$t('orders.headline')} />
			<div class="flex flex-col md:flex-row justify-between items-center pt-6">
				<span class="flex-1 font-semibold text-h-12">
					{shownOrderLineItems.length}{' '}
					<T
						keyName={shownOrderLineItems.length === 1
							? 'orders.product.label'
							: 'orders.products.label'}
					/>
				</span>
				<SearchInput bind:value={searchString} />
			</div>
		</div>
		<div
			class="flex flex-col items-stretch justify-between overflow-hidden lg:items-center lg:space-x-2 lg:flex-row space-y-7 lg:space-y-0"
		>
			<MonthSlider
				on:previousMonthClick={() => {
					handleMonthSliderClick(true);
				}}
				on:nextMonthClick={() => {
					handleMonthSliderClick(false);
				}}
				{selectedMonth}
				minDate={new Date($customer?.createdAt ?? new Date())}
				maxDate={new Date()}
				minLength={130}
			/>

			<div class="py-2 pl-2 overflow-auto rounded-md scrollbar-hide bg-accent-primary_005">
				<div class="flex space-x-1">
					{#each Array.from(new Set(ORDER_STATE_MAPPING)) as status}
						<div class="last:pr-2">
							<LabelledChip
								on:click={() => addFilter(status)}
								additionalClass="bg-white whitespace-nowrap"
								label={$t(`orderStateMapping.${status}`)}
								selected={activeFilter === status}
							/>
						</div>
					{/each}
				</div>
			</div>
		</div>
	</div>
	{#if shownOrderLineItems.length > 0}
		<OrderLineItemList orderLineItems={shownOrderLineItems} />
	{:else}
		<h4 class="px-4 mt-8 text-center">
			{$t('orders.month.empty', { month: formatMonthString(selectedMonth, ' ') })}
		</h4>
	{/if}
</div>
